/**
 * Client-side SSE parsing.
 *
 * The Worker proxy has two modes:
 * - `protocol`: `event: chat.delta|chat.done|chat.error` + JSON data (default).
 * - `deepseek-raw`: raw DeepSeek chunks; this parser also accepts them so the
 *   PWA can fall back if the Worker is older than the PWA.
 */
import type {
  ChatDelta,
  ChatDone,
  ChatError,
  ChatError as ProtocolChatError,
  ModelSelection,
  Usage,
} from '@luminis/protocol';

export interface SSERawEvent {
  event: string;
  data: string;
}

/** Generic SSE frame decoder; tolerates arbitrary chunk boundaries and CRLF. */
export class SSEDecoder {
  private buffer = '';
  private eventName = '';
  private dataLines: string[] = [];

  push(chunk: string): SSERawEvent[] {
    const events: SSERawEvent[] = [];
    this.buffer += chunk;
    let newline = this.buffer.indexOf('\n');
    while (newline >= 0) {
      const line = this.buffer.slice(0, newline).replace(/\r$/u, '');
      this.buffer = this.buffer.slice(newline + 1);
      const event = this.handleLine(line);
      if (event) events.push(event);
      newline = this.buffer.indexOf('\n');
    }
    return events;
  }

  finish(): SSERawEvent[] {
    const events: SSERawEvent[] = [];
    if (this.buffer.length > 0) {
      const event = this.handleLine(this.buffer.replace(/\r$/u, ''));
      this.buffer = '';
      if (event) events.push(event);
    }
    const flushed = this.flush();
    if (flushed) events.push(flushed);
    return events;
  }

  private handleLine(line: string): SSERawEvent | null {
    if (line === '') return this.flush();
    if (line.startsWith(':')) return null;
    const colon = line.indexOf(':');
    const field = colon >= 0 ? line.slice(0, colon) : line;
    const rawValue = colon >= 0 ? line.slice(colon + 1) : '';
    const value = rawValue.startsWith(' ') ? rawValue.slice(1) : rawValue;
    if (field === 'event') this.eventName = value;
    else if (field === 'data') this.dataLines.push(value);
    return null;
  }

  private flush(): SSERawEvent | null {
    if (this.dataLines.length === 0 && this.eventName === '') return null;
    const event: SSERawEvent = {
      event: this.eventName || 'message',
      data: this.dataLines.join('\n'),
    };
    this.eventName = '';
    this.dataLines = [];
    return event;
  }
}

export type ChatStreamEvent =
  | { type: 'delta'; delta: ChatDelta }
  | { type: 'done'; done: ChatDone }
  | { type: 'error'; error: ChatError };

export type ChatSSEMode = 'protocol' | 'deepseek-raw';

export interface ChatSSEParserOptions {
  mode: ChatSSEMode;
  messageId: string;
  model: ModelSelection;
}

interface DeepSeekRawChunk {
  model?: string;
  choices?: Array<{
    delta?: { content?: string | null; reasoning_content?: string | null };
    finish_reason?: string | null;
  }>;
  usage?: {
    prompt_tokens?: number;
    completion_tokens?: number;
    prompt_cache_hit_tokens?: number;
  } | null;
  error?: { message?: string };
}

export class ChatSSEParser {
  private readonly decoder: SSEDecoder;
  private readonly options: ChatSSEParserOptions;
  private seq = 0;
  private done = false;
  private usage: Usage | undefined;
  private finishReason: string | undefined;
  private reportedModel: string | undefined;

  constructor(options: ChatSSEParserOptions) {
    this.options = options;
    this.decoder = new SSEDecoder();
  }

  get isDone(): boolean {
    return this.done;
  }

  push(chunk: string): ChatStreamEvent[] {
    return this.decoder.push(chunk).flatMap((event) => this.handleEvent(event));
  }

  finish(): ChatStreamEvent[] {
    const events = this.decoder.finish().flatMap((event) => this.handleEvent(event));
    if (!this.done) events.push(this.emitDone());
    return events;
  }

  private handleEvent(raw: SSERawEvent): ChatStreamEvent[] {
    if (this.done) return [];
    if (this.options.mode === 'protocol') return this.handleProtocolEvent(raw);
    return this.handleDeepSeekRawEvent(raw);
  }

  private handleProtocolEvent(raw: SSERawEvent): ChatStreamEvent[] {
    try {
      if (raw.event === 'chat.delta') {
        const delta = JSON.parse(raw.data) as ChatDelta;
        if (typeof delta.delta !== 'string') return [];
        this.seq = Math.max(this.seq, delta.seq);
        return [{ type: 'delta', delta }];
      }
      if (raw.event === 'chat.done') {
        const done = JSON.parse(raw.data) as ChatDone;
        this.done = true;
        return [{ type: 'done', done }];
      }
      if (raw.event === 'chat.error') {
        const error = JSON.parse(raw.data) as ProtocolChatError;
        this.done = true;
        return [{ type: 'error', error }];
      }
      return [];
    } catch {
      this.done = true;
      return [
        {
          type: 'error',
          error: {
            message_id: this.options.messageId,
            code: 'INTERNAL',
            message: '无法解析 Worker 流式响应',
            retryable: false,
          },
        },
      ];
    }
  }

  private handleDeepSeekRawEvent(raw: SSERawEvent): ChatStreamEvent[] {
    if (raw.event !== 'message') return [];
    const payload = raw.data.trim();
    if (payload === '') return [];
    if (payload === '[DONE]') {
      this.done = true;
      return [{ type: 'done', done: this.buildDone() }];
    }
    let chunk: DeepSeekRawChunk;
    try {
      chunk = JSON.parse(payload) as DeepSeekRawChunk;
    } catch {
      this.done = true;
      return [
        {
          type: 'error',
          error: {
            message_id: this.options.messageId,
            code: 'INTERNAL',
            message: '上游流式响应解析失败',
            retryable: false,
          },
        },
      ];
    }
    if (chunk.error?.message) {
      this.done = true;
      return [
        {
          type: 'error',
          error: {
            message_id: this.options.messageId,
            code: 'INTERNAL',
            message: chunk.error.message.slice(0, 300),
            retryable: false,
          },
        },
      ];
    }
    if (chunk.model) this.reportedModel = chunk.model;
    if (chunk.usage) {
      this.usage = {
        input_tokens: chunk.usage.prompt_tokens ?? 0,
        output_tokens: chunk.usage.completion_tokens ?? 0,
        ...(typeof chunk.usage.prompt_cache_hit_tokens === 'number'
          ? { cached_input_tokens: chunk.usage.prompt_cache_hit_tokens }
          : {}),
      };
    }
    const events: ChatStreamEvent[] = [];
    const choice = chunk.choices?.[0];
    if (choice?.finish_reason) this.finishReason = choice.finish_reason;
    const delta = choice?.delta;
    if (delta?.reasoning_content) {
      this.seq += 1;
      events.push({
        type: 'delta',
        delta: {
          message_id: this.options.messageId,
          seq: this.seq,
          kind: 'reasoning',
          delta: delta.reasoning_content,
        },
      });
    }
    if (delta?.content) {
      this.seq += 1;
      events.push({
        type: 'delta',
        delta: {
          message_id: this.options.messageId,
          seq: this.seq,
          kind: 'content',
          delta: delta.content,
        },
      });
    }
    return events;
  }

  private buildDone(): ChatDone {
    return {
      message_id: this.options.messageId,
      model: {
        ...this.options.model,
        ...(this.reportedModel ? { model: this.reportedModel } : {}),
      },
      ...(this.usage ? { usage: this.usage } : {}),
      ...(this.finishReason ? { finish_reason: this.finishReason } : {}),
    };
  }

  private emitDone(): ChatStreamEvent {
    this.done = true;
    return { type: 'done', done: this.buildDone() };
  }
}
